import React from "react";
import { styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Container, Typography, Grid, Box } from "@mui/material";
import { v4FlatWinsAtBreakpointsSx } from "../../../theme/v4CompatSx";
import appleappstore from "../../Modals/app-store-logo.png";
import googleplaystore from "../../Modals/google-play-logo.png";
import { isAndroid } from "react-device-detect";
import { APP_STORE_URL, PLAY_STORE_URL } from "./links";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * The "Install the Free App" block at the foot of /free-trial. Styling-only
 * conversion — the links and their `target`/`rel` attributes were
 * byte-identical.
 *
 * #1088: moved here unchanged from `FreeTrial/InstallApp.tsx` as the `footer`
 * layout of the shared `InstallApp`. Every styled block and sx helper below,
 * including both parity restores, is the PM5 T2 code as measured; only the
 * import paths moved and the link URLs now live in `./links.ts`.
 * /free-trial is a money path — change nothing here without a before/after
 * capture of that page.
 *
 * #1264: links only, no styling change. The smart.links (and their
 * `ga`/`sc`/`tt` campaign variants) sent phones to the sign-up page, not a
 * store, so each badge now goes to its own store and the app icon to the
 * phone's own store (the App Store on an iPhone or a computer, Google Play on
 * Android).
 */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavy,
  padding: theme.spacing(6, 0, 0, 0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(8, 0, 0, 0),
  },
}));

// was: useStyles().appIconGraphic — a bare <img>.
// `borderRadius: 10` is written as "10px" here: inside `styled()` a bare
// number would still be px, but sx's `borderRadius` IS shape-scaled (guide §2),
// so spelling the unit removes any chance of the value being copied into an
// `sx` block later and silently becoming 40px.
const AppIconGraphic = styled("img")(({ theme }) => ({
  width: "80px",
  borderRadius: "10px",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    width: "130px",
    marginBottom: theme.spacing(1),
  },
}));

// was: useStyles().appStoreLogo — a bare <img>, rendered twice.
const AppStoreLogo = styled("img")(({ theme }) => ({
  maxWidth: "140px",
  margin: "5px",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    margin: "10px 5px",
    maxWidth: "130px",
  },
  [theme.breakpoints.up("md")]: {
    margin: "10px 5px",
    maxWidth: "180px",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2rem",
  lineHeight: "2.2rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  "& span": {
    color: "background.ffGreen",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "3rem",
    lineHeight: "3.2rem",
  },
});

// was: useStyles().titleLarge
const titleLargeSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2.8rem",
  lineHeight: "2.8rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "5.4rem",
    lineHeight: "5rem",
  },
});

// was: useStyles().buttonContainer
const buttonContainerSx = (theme: Theme) => ({
  textAlign: "center",
  margin: "8px 0px 24px 0px",
  padding: "8px",
  [theme.breakpoints.up("sm")]: {
    textAlign: "center",
  },
});

// was: useStyles().downloadText
// PARITY RESTORE (guide §2, "A flat `sx` declaration loses to the component's
// own breakpoint rule") — this rule is flat-only, and it is applied to a
// `<Typography variant="h4">` whose theme tokens declare `fontSize`/`lineHeight`
// again at `sm` (1.4rem/1.5rem) and `md` (1.5rem/1.6rem). In v4 the file's JSS
// sheet index beat those at every width; in v9 they are hoisted after the flat
// keys of the same emotion class and win. Measured on the built /free-trial page
// at 1440: "Download at:" rendered 24px/25.6px instead of v4's 17.6px/19.2px,
// which pushed the store badges and everything below them 6.4px down the page.
const downloadTextSx = (theme: Theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      letterSpacing: "-1px",
      fontSize: "1.1rem",
      lineHeight: "1.2rem",
      fontWeight: 400,
      textShadow: "-2px 2px 2px #171717",
      marginBottom: "8px",
      color: "#ddd",
    },
    ["fontSize", "lineHeight"],
  );

// was: useStyles().purchaseText
// PARITY RESTORE — same flat-vs-variant-breakpoint case as `downloadTextSx`
// above ("Free Download. No Payment Required." measured 24px/25.6px against
// v4's 17.6px/19.2px at 1440).
const purchaseTextSx = (theme: Theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      letterSpacing: "-1px",
      fontSize: "1.1rem",
      lineHeight: "1.2rem",
      fontWeight: 500,
      textShadow: "-2px 2px 2px #171717",
      marginTop: "24px",
      marginBottom: "8px",
      color: "#ddd",
    },
    ["fontSize", "lineHeight"],
  );

const FooterLayout = () => {
  const iconHref = isAndroid ? PLAY_STORE_URL : APP_STORE_URL;

  return (
    <>
      <Background>
        <Container maxWidth="md" fixed>
          {/* v5+ Grid: `item` is gone and the breakpoint props collapse into
              `size`. No `spacing` on this container, so widths are unchanged. */}
          <Grid container>
            <Grid size={{ xs: 12, sm: 12 }}>
              <Box style={{ display: "flex" }}>
                {/* v9 removed Box's shorthand system props — `marginRight={2}`
                    would be forwarded to the DOM and apply nothing (guide §2,
                    "Box system props"). Rewritten as an `sx` key; the value
                    keeps its meaning (spacing multiple ⇒ 16px). */}
                <Box sx={{ marginRight: 2 }}>
                  <a href={iconHref} target="_blank" rel="noopener noreferrer">
                    <AppIconGraphic
                      src={
                        "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/app_logo_200_dark.jpg"
                      }
                      alt="FarmFLiX App Icon"
                    />
                  </a>
                </Box>
                <Box>
                  <Typography variant="h2" sx={titleSx}>
                    Install The
                  </Typography>
                  <Typography variant="h2" sx={titleLargeSx}>
                    Free App
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 4, sm: 4 }} style={{ textAlign: "right" }}></Grid>
            <Grid size={{ xs: 12, sm: 12 }} sx={buttonContainerSx}>
              <Typography variant="h4" sx={downloadTextSx}>
                Download at:
              </Typography>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <AppStoreLogo
                  src={appleappstore}
                  alt="Apple App Store Graphic"
                />
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppStoreLogo
                  src={googleplaystore}
                  alt="Google Play Store Graphic"
                />
              </a>
              <Typography variant="h4" sx={purchaseTextSx}>
                Free Download. No Payment Required.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Background>
    </>
  );
};

export default FooterLayout;
